import React, { useState } from "react";

const AlphaNumericKeypad = ({
  onKeyPress,
  onDelete,
  onLayoutChange,
}: {
  onKeyPress: (key: string) => void;
  onDelete: () => void;
  onLayoutChange?: () => void;
}) => {
  const [isUpperCase, setIsUpperCase] = useState(true);
  const [isNumericLayout, setIsNumericLayout] = useState(true);

  const baseAlphaRows = [
    ["A", "Z", "E", "R"],
    ["T", "Y", "U", "I"],
    ["O", "P", "Q", "S"],
    ["D", "F", "G", "H"],
    ["J", "K", "L", "M"],
    ["W", "X", "C", "V"],
    ["B", "N", "TOGGLE_CASE"], 
    ["⌫", "TO_NUM"], 
  ];
  
  const numericRows = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["0", "_", "-"],
    ["⌫", "TO_ALPHA"], 
  ];
  
  const getAlphaRows = () => {
    return baseAlphaRows.map((row) =>
      row.map((key) => {
        if (/^[A-Z]$/i.test(key)) {
          return isUpperCase ? key.toUpperCase() : key.toLowerCase();
        }
        return key;
      })
    );
  };
  const renderKeyLabel = (key: string) => {
    switch (key) {
      case "⌫":
        return <i className="fas fa-backspace"></i>;
      case "TOGGLE_CASE":
        return <i className="fas fa-text-height"></i>; // ou fa-font
      case "TO_ALPHA":
        return <i className="fas fa-keyboard"></i>;
      case "TO_NUM":
        return <i className="fas fa-hashtag"></i>;
      default:
        return key;
    }
  };
  
  const [rows, setRows] = useState(numericRows);

  const isDelete = (key: string) => key === "⌫";
  const isChangeLayout = (key: string) => key === "?123" || key === "ABC";
  const isToggleCase = (key: string) => ["min", "MAJ"].includes(key);

  const isNumber = (key: string) => /^[0-9]$/.test(key);
  const isSpecial = (key: string) => ["_", "-"].includes(key);
  const isLetter = (key: string) => /^[a-zA-Z]$/.test(key);

  const getKeyStyle = (key: string): React.CSSProperties => {
    const style: React.CSSProperties = { ...styles.key };

    if (isNumber(key)) {
      style.backgroundColor = "#d0f0fd";
      style.color = "#0077b6";
    } else if (isLetter(key)) {
      style.backgroundColor = "#e0f7e9";
      style.color = "#2a9d8f";
    } else if (isSpecial(key)) {
      style.backgroundColor = "#fff3cd";
      style.color = "#b36b00";
    } else if (isDelete(key)) {
      return { ...styles.key, ...styles.deleteKey };
    }

    return style;
  };

  const onClickButton = (key: string) => {
    if (key === "TO_ALPHA") {
      setRows(getAlphaRows());
      setIsNumericLayout(false);
      onLayoutChange?.();
    } else if (key === "TO_NUM") {
      setRows(numericRows);
      setIsNumericLayout(true);
      onLayoutChange?.();
    } else if (key === "TOGGLE_CASE") {
      setIsUpperCase((prev) => !prev);
      if (!isNumericLayout) setRows(getAlphaRows());
    } else {
      isDelete(key) ? onDelete() : onKeyPress(key);
    }
  };
  

  return (
    <div style={styles.container as React.CSSProperties}>
      {rows.map((row, i) => (
        <div key={i} style={styles.row as React.CSSProperties}>
          {row.map((key, j) => (
            <button
              key={j}
              onClick={() => onClickButton(key)}
              style={getKeyStyle(key)}
            >
              {renderKeyLabel(key)}

            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "15px",
    border: "1px solid #ddd",
    borderRadius: "10px",
    backgroundColor: "#fff",
    maxWidth: "1000px",
    margin: "10px auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  row: {
    display: "flex",
    justifyContent: "center",
    gap: "5px",
    flexWrap: "wrap",
  },
  key: {
    padding: "12px 16px",
    fontSize: "30px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    cursor: "pointer",
    minWidth: "100px",
    textAlign: "center" as const,
    transition: "background-color 0.2s",
  },
  deleteKey: {
    backgroundColor: "#ffe5e5",
    color: "#c00",
    fontWeight: "bold",
  },
};

export default AlphaNumericKeypad;